import * as React from 'react'
import { usePrevious } from 'reactUtils'
import styled from 'styled-typed'
import { withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { Navigation, QueryData } from 'navigation'
import { t } from 'translations'

const SelectorWrapper = styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`
const PresetOption = styled.div`
    font-size: 0.875rem;
    height: 2.2rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: ${(p) => p.theme.colors.textLight};
    border: 1px solid transparent;
    cursor: pointer;
    padding: 0 0.8em;

    &.selected {
        color: ${(p) => p.theme.colors.textDark};
        background: ${(p) => p.theme.colors.white};
        border: 1px solid ${(props) => props.theme.colors.border};
        border-radius: 6px;
    }
`

export type PresetType =
    | 'today'
    | 'yesterday'
    | 'thisWeek'
    | 'thisMonth'
    | 'weekdays'
    | 'weekends'
    | '7days'
    | '4weeks'
    | 'last7days'
    | 'last4weeks'
    | 'last30days'
    | 'last90days'
    | 'lifetime'
    | 'revenue'
    | 'transactions'
    | 'products_sold'
    | 'order_value'
    | 'cart_size'
    | 'drop_rates'
    | 'back_rates'

interface PresetOptions {
    value: PresetType
    name: string
}

// A function so the labels are translated when they are shown, not when this module loads.
const getAllPresetOptions = (): PresetOptions[] => [
    { value: 'today', name: t('uiComponents.popups.presets.today') },
    { value: 'yesterday', name: t('uiComponents.popups.presets.yesterday') },
    { value: 'thisWeek', name: t('uiComponents.popups.presets.thisWeek') },
    { value: 'thisMonth', name: t('uiComponents.popups.presets.thisMonth') },
    { value: 'weekdays', name: t('uiComponents.popups.presets.weekdays') },
    { value: 'weekends', name: t('uiComponents.popups.presets.weekends') },
    { value: 'last7days', name: t('uiComponents.popups.presets.last7Days') },
    { value: 'last4weeks', name: t('uiComponents.popups.presets.last4Weeks') },
    { value: 'last30days', name: t('uiComponents.popups.presets.last30Days') },
    { value: 'last90days', name: t('uiComponents.popups.presets.last90Days') },
    { value: 'lifetime', name: t('uiComponents.popups.presets.lifetime') },
    { value: '7days', name: t('uiComponents.popups.presets.sevenDays') },
    { value: '4weeks', name: t('uiComponents.popups.presets.fourWeeks') },
    { value: 'revenue', name: t('uiComponents.popups.presets.revenue') },
    { value: 'transactions', name: t('uiComponents.popups.presets.transactions') },
    { value: 'products_sold', name: t('uiComponents.popups.presets.productsSold') },
    { value: 'order_value', name: t('uiComponents.popups.presets.orderValue') },
    { value: 'cart_size', name: t('uiComponents.popups.presets.cartSize') },
    { value: 'drop_rates', name: t('uiComponents.popups.presets.exitRate') },
    { value: 'back_rates', name: t('uiComponents.popups.presets.backwardsRate') },
]

interface PresetsElementProps {
    presetName: string
    default: PresetType
    options: PresetType[]
    navigation: Navigation
    match: RouteMatch<{}>
    onChange?: (v: string) => void
    style?: React.CSSProperties
}

function PresetsElement(props: PresetsElementProps) {
    const [value, setValue] = React.useState<string>(props.default)

    const prevQuery: QueryData = usePrevious(props.navigation.query()) || {}
    React.useEffect(() => {
        const newQuery = props.navigation.query()
        if (newQuery[props.presetName] !== value && newQuery[props.presetName] !== prevQuery[props.presetName]) {
            setValue(newQuery[props.presetName])
        }
    }, [props.navigation.query()])

    const onChange = (v: string) => {
        setValue(v)
        props.navigation.addQueryWithReplace({ [props.presetName]: v })
        if (props.onChange) {
            props.onChange(v)
        }
    }

    const options = getAllPresetOptions().filter((o) => props.options.indexOf(o.value) > -1)

    return (
        <SelectorWrapper onClick={(ev) => ev.stopPropagation()} style={props.style}>
            {options.map((o) => (
                <PresetOption
                    key={o.value}
                    className={value === o.value ? 'selected' : ''}
                    onClick={() => onChange(o.value)}
                    data-userpilot={o.value}
                >
                    {o.name}
                </PresetOption>
            ))}
        </SelectorWrapper>
    )
}

export default withNavigation(PresetsElement)
